import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Play, Pause, Trash2, Plus, ArrowRight, Send, X, Settings } from 'lucide-react';
import { getSearches, startSearch, pauseSearch, deleteSearch, api } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface SearchItem {
  id: number;
  name: string;
  url: string;
  status: 'ACTIVE' | 'PAUSED' | 'EXPIRED' | 'ERROR' | 'DELETED';
  source: string;
  last_checked_at: string | null;
  created_at: string;
  notification_chat_id: number | null;
  notification_chat_title: string | null;
}

interface HiddenSeller {
  id: number;
  seller_name: string | null;
  anonymous: boolean;
  listing_title: string | null;
}

interface HiddenData {
  can_hide: boolean;
  items: HiddenSeller[];
}

export const SearchesList: React.FC = () => {
  const [searches, setSearches] = useState<SearchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canUseChannel = !!user?.active_subscription?.plan?.group_enabled;
  const [hidden, setHidden] = useState<Record<number, HiddenData | 'loading'>>({});

  const loadHidden = async (searchId: number) => {
    setHidden((h) => ({ ...h, [searchId]: 'loading' }));
    try {
      const res = await api.get(`/searches/${searchId}/hidden-sellers`);
      setHidden((h) => ({ ...h, [searchId]: { can_hide: !!res.data.can_hide, items: res.data.items || [] } }));
    } catch (e: any) {
      setHidden((h) => { const n = { ...h }; delete n[searchId]; return n; });
      alert('Ошибка: ' + (e.response?.data?.detail || 'не удалось загрузить список'));
    }
  };

  const toggleHidden = (searchId: number) => {
    if (hidden[searchId]) {
      setHidden((h) => { const n = { ...h }; delete n[searchId]; return n; });
    } else {
      loadHidden(searchId);
    }
  };

  const unhideSeller = async (searchId: number, blockedId: number) => {
    try {
      await api.delete(`/searches/${searchId}/hidden-sellers/${blockedId}`);
    } catch (e: any) {
      if (e.response?.status !== 404) alert('Ошибка: ' + (e.response?.data?.detail || 'не удалось разблокировать'));
    }
    loadHidden(searchId);
  };

  const sellerLabel = (it: HiddenSeller) =>
    it.anonymous
      ? `Пользователь${it.listing_title ? ` (из объявления «${it.listing_title.slice(0, 40)}»)` : ''}`
      : (it.seller_name || 'Продавец');

  const openChannelBind = (searchId: number) => {
    const tg = (window as any).Telegram?.WebApp;
    const url = `https://t.me/avigatr_bot?start=bindchannel_${searchId}`;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(url);
    } else {
      window.open(url, '_blank');
    }
  };

  const unbindChannel = async (searchId: number) => {
    try {
      await api.post(`/searches/${searchId}/unbind-channel`);
      loadSearches();
    } catch (e: any) {
      alert('Ошибка: ' + (e.response?.data?.detail || 'не удалось отвязать канал'));
    }
  };

  useEffect(() => {
    loadSearches();
  }, []);

  const loadSearches = async () => {
    try {
      const data = await getSearches();
      setSearches(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id: number, currentStatus: string) => {
    try {
      if (currentStatus === 'ACTIVE') {
        await pauseSearch(id);
      } else {
        await startSearch(id);
      }
      loadSearches();
    } catch (e) {
      alert('Ошибка: ' + ((e as any).response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Удалить поиск? Это действие нельзя отменить.')) return;
    try {
      await deleteSearch(id);
      loadSearches();
    } catch (e) {
      alert('Ошибка удаления');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return '#10b981';
      case 'PAUSED': return '#f59e0b';
      default: return '#9ca3af';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'Активен';
      case 'PAUSED': return 'На паузе';
      default: return 'Активен';
    }
  };

  if (loading) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📋 Мои поиски</h1>
        <button
          style={styles.addBtn}
          onClick={() => navigate('/add')}
        >
          <Plus size={18} />
        </button>
      </div>

      {searches.length === 0 ? (
        <div style={styles.empty}>
          <Search size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
          <p style={styles.emptyText}>Нет активных поисков</p>
          <button
            style={styles.emptyButton}
            onClick={() => navigate('/add')}
          >
            Добавить первый поиск
          </button>
        </div>
      ) : (
        <div style={styles.list}>
          {searches.map((s) => (
            <div key={s.id} style={styles.card}>
              <div style={styles.cardTop}>
                <div style={styles.cardLeft}>
                  <div style={styles.nameRow}>
                    <div
                      style={{
                        ...styles.statusDot,
                        backgroundColor: getStatusColor(s.status),
                      }}
                    />
                    <span style={styles.name}>{s.name}</span>
                  </div>
                  <span style={styles.statusText}>{getStatusText(s.status)}</span>
                  <span style={styles.source}>{s.source.toUpperCase()}</span>
                </div>
                <div style={styles.actions}>
                  <button
                    style={styles.iconBtn}
                    onClick={() => handleToggle(s.id, s.status)}
                    title={s.status === 'ACTIVE' ? 'Поставить на паузу' : 'Запустить'}
                  >
                    {s.status === 'ACTIVE' ? <Pause size={18} /> : <Play size={18} />}
                  </button>
                  <button
                    style={styles.iconBtn}
                    onClick={() => navigate(`/searches/${s.id}/edit`)}
                    title="Настройки поиска"
                  >
                    <Settings size={18} />
                  </button>
                  <button
                    style={{ ...styles.iconBtn, color: '#ef4444' }}
                    onClick={() => handleDelete(s.id)}
                    title="Удалить"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.link}
              >
                Открыть на Avito <ArrowRight size={14} />
              </a>

              {canUseChannel && (
                s.notification_chat_id ? (
                  <div style={styles.channelRow}>
                    <Send size={14} style={{ color: '#10b981' }} />
                    <span style={styles.channelText}>
                      {s.notification_chat_title || 'Канал привязан'}
                    </span>
                    <button style={styles.channelUnbindBtn} onClick={() => unbindChannel(s.id)} title="Отвязать канал">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <button style={styles.channelBindBtn} onClick={() => openChannelBind(s.id)}>
                    <Send size={14} /> Привязать канал
                  </button>
                )
              )}

              <button style={styles.channelBindBtn} onClick={() => toggleHidden(s.id)}>
                🚫 Скрытые продавцы
                {hidden[s.id] && hidden[s.id] !== 'loading' ? ` (${(hidden[s.id] as HiddenData).items.length})` : ''}
              </button>
              {hidden[s.id] === 'loading' && <div style={styles.hiddenHint}>Загрузка…</div>}
              {hidden[s.id] && hidden[s.id] !== 'loading' && (
                <div style={styles.hiddenBox}>
                  {(hidden[s.id] as HiddenData).items.length === 0 ? (
                    <div style={{ ...styles.hiddenHint, margin: 0 }}>
                      Список пуст. Скрыть продавца можно кнопкой «🚫 Скрыть продавца» под уведомлением в Telegram
                      {(hidden[s.id] as HiddenData).can_hide ? '.' : ' (тарифы PRO и EXPERT).'}
                    </div>
                  ) : (
                    (hidden[s.id] as HiddenData).items.map((it) => (
                      <div key={it.id} style={styles.hiddenRow}>
                        <span style={styles.hiddenName}>{sellerLabel(it)}</span>
                        <button style={styles.unhideBtn} onClick={() => unhideSeller(s.id, it.id)}>
                          Разблокировать
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '16px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #e5e7eb',
    borderTop: '3px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    margin: 0,
    color: 'var(--tg-theme-text-color, #000)',
  },
  addBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: 'var(--tg-theme-button-color, #2481cc)',
    color: '#fff',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyText: {
    fontSize: '16px',
    color: 'var(--tg-theme-hint-color, #666)',
    marginBottom: '16px',
  },
  emptyButton: {
    padding: '12px 24px',
    backgroundColor: 'var(--tg-theme-button-color, #2481cc)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  card: {
    backgroundColor: 'var(--tg-theme-secondary-bg-color, #fff)',
    borderRadius: '16px',
    padding: '14px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  cardLeft: {
    flex: 1,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  name: {
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--tg-theme-text-color, #000)',
  },
  statusText: {
    fontSize: '13px',
    color: 'var(--tg-theme-hint-color, #666)',
    marginLeft: '16px',
  },
  source: {
    fontSize: '11px',
    color: '#9ca3af',
    marginLeft: '16px',
    marginTop: '2px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  iconBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: 'var(--tg-theme-bg-color, #f5f5f5)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--tg-theme-text-color, #000)',
  },
  link: {
    fontSize: '13px',
    color: 'var(--tg-theme-link-color, #2481cc)',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: '16px',
  },
  channelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '8px',
    marginLeft: '16px',
    fontSize: '13px',
  },
  channelText: {
    color: 'var(--tg-theme-text-color, #000)',
    flex: 1,
  },
  channelUnbindBtn: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    padding: '2px',
  },
  channelBindBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '8px',
    marginLeft: '16px',
    background: 'none',
    border: 'none',
    color: 'var(--tg-theme-link-color, #2481cc)',
    fontSize: '13px',
    cursor: 'pointer',
    padding: 0,
  },
  hiddenBox: {
    marginTop: '8px',
    marginLeft: '16px',
    padding: '8px 10px',
    borderRadius: '10px',
    backgroundColor: 'var(--tg-theme-bg-color, #f5f5f5)',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  hiddenRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
  },
  hiddenName: {
    flex: 1,
    color: 'var(--tg-theme-text-color, #000)',
  },
  hiddenHint: {
    fontSize: '12px',
    color: 'var(--tg-theme-hint-color, #666)',
    marginTop: '6px',
    marginLeft: '16px',
    lineHeight: 1.4,
  },
  unhideBtn: {
    background: 'none',
    border: '1px solid var(--tg-theme-link-color, #2481cc)',
    color: 'var(--tg-theme-link-color, #2481cc)',
    borderRadius: '8px',
    padding: '4px 8px',
    fontSize: '12px',
    cursor: 'pointer',
    flexShrink: 0,
  },
};
